<template>
    <div class="doc-editor" :class="{ 'doc-editor--readonly': disabled }">
        <!-- Toolbar -->
        <div v-if="editor && !disabled" class="doc-editor-toolbar">
            <!-- Text style -->
            <div class="doc-toolbar-group">
                <div class="doc-editor-btn" v-tooltip="'Bold'"
                    :class="{ 'is-active': editor.isActive('bold') }"
                    @click="editor.chain().focus().toggleBold().run()">
                    <strong style="font-size:13px;">B</strong>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Italic'"
                    :class="{ 'is-active': editor.isActive('italic') }"
                    @click="editor.chain().focus().toggleItalic().run()">
                    <em style="font-size:13px;">I</em>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Underline'"
                    :class="{ 'is-active': editor.isActive('underline') }"
                    @click="editor.chain().focus().toggleUnderline().run()">
                    <u style="font-size:13px;">U</u>
                </div>
            </div>

            <div class="doc-toolbar-sep"></div>

            <!-- Headings -->
            <div class="doc-toolbar-group">
                <div class="doc-editor-btn" v-tooltip="'Heading 1'"
                    :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                    @click="editor.chain().focus().toggleHeading({ level: 1 }).run()">
                    <span style="font-size:11px;font-weight:700;">H1</span>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Heading 2'"
                    :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
                    <span style="font-size:11px;font-weight:700;">H2</span>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Heading 3'"
                    :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                    @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
                    <span style="font-size:11px;font-weight:700;">H3</span>
                </div>
            </div>

            <div class="doc-toolbar-sep"></div>

            <!-- Code block -->
            <div class="doc-toolbar-group">
                <div class="doc-editor-btn" v-tooltip="'Inline code'"
                    :class="{ 'is-active': editor.isActive('code') }"
                    @click="editor.chain().focus().toggleCode().run()">
                    <v-icon x-small>mdi-code-tags</v-icon>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Code block'"
                    :class="{ 'is-active': editor.isActive('codeBlock') }"
                    @click="editor.chain().focus().toggleCodeBlock().run()">
                    <v-icon x-small>mdi-code-braces</v-icon>
                </div>
            </div>

            <div class="doc-toolbar-sep"></div>

            <!-- Lists -->
            <div class="doc-toolbar-group">
                <div class="doc-editor-btn" v-tooltip="'Bullet list'"
                    :class="{ 'is-active': editor.isActive('bulletList') }"
                    @click="editor.chain().focus().toggleBulletList().run()">
                    <v-icon x-small>mdi-format-list-bulleted</v-icon>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Ordered list'"
                    :class="{ 'is-active': editor.isActive('orderedList') }"
                    @click="editor.chain().focus().toggleOrderedList().run()">
                    <v-icon x-small>mdi-format-list-numbered</v-icon>
                </div>
            </div>

            <div class="doc-toolbar-sep"></div>

            <!-- Block extras -->
            <div class="doc-toolbar-group">
                <div class="doc-editor-btn" v-tooltip="'Blockquote'"
                    :class="{ 'is-active': editor.isActive('blockquote') }"
                    @click="editor.chain().focus().toggleBlockquote().run()">
                    <v-icon x-small>mdi-format-quote-close</v-icon>
                </div>
                <div class="doc-editor-btn" v-tooltip="'Horizontal rule'"
                    @click="editor.chain().focus().setHorizontalRule().run()">
                    <v-icon x-small>mdi-minus</v-icon>
                </div>
            </div>

            <div class="doc-toolbar-sep"></div>

            <!-- Image -->
            <div class="doc-toolbar-group">
                <div class="doc-editor-btn" v-tooltip="'Insert image (paste or click)'"
                    @click="$refs.imgInput.click()">
                    <v-icon x-small>mdi-image-plus</v-icon>
                </div>
                <input ref="imgInput" type="file" accept="image/*" style="display:none" @change="insertImageFile">
            </div>
        </div>

        <!-- Editor content -->
        <editor-content class="doc-editor-content" :editor="editor" />
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'

export default {
    name: 'DocEditor',
    components: { EditorContent },

    props: {
        value:    { type: String, default: '' },
        disabled: { type: Boolean, default: false },
    },

    data: () => ({ editor: null }),

    watch: {
        value(val) {
            if (!this.editor || this.editor.getHTML() === val) return
            this.editor.commands.setContent(val, false)
        },
        disabled(val) {
            if (this.editor) this.editor.setEditable(!val)
        },
    },

    mounted() {
        this.editor = new Editor({
            injectCSS: false,
            editable: !this.disabled,
            content:  this.value,
            extensions: [
                StarterKit,
                Underline,
                Image.configure({ inline: false, allowBase64: true }),
            ],
            onUpdate: () => {
                this.$emit('input', this.editor.getHTML())
            },
            transformPastedHTML: this.cleanWordHtml,
        })
    },

    beforeDestroy() {
        this.editor?.destroy()
    },

    methods: {
        cleanWordHtml(html) {
            // Remove Word conditional comments
            html = html.replace(/<!--\[if[\s\S]*?\[endif\]-->/gi, '')
            // Remove Word XML namespace tags (o:p, w:*, m:*)
            html = html.replace(/<\/?o:[^>]*>/gi, '')
            html = html.replace(/<\/?w:[^>]*>/gi, '')
            html = html.replace(/<\/?m:[^>]*>/gi, '')
            // Strip mso-* and Office-specific CSS properties from style attributes
            html = html.replace(/style="([^"]*)"/gi, (_, styles) => {
                const cleaned = styles
                    .split(';')
                    .filter(s => !/^\s*(mso-|panose-|font-variant|text-autospace|text-kashida|layout-|line-break|word-break:keep-all)/i.test(s))
                    .join(';')
                    .replace(/;\s*$/, '')
                return cleaned.trim() ? `style="${cleaned}"` : ''
            })
            // Remove class attributes with MSO/Word classes
            html = html.replace(/\s*class="[^"]*Mso[^"]*"/gi, '')
            // Remove empty spans left over
            html = html.replace(/<span\s*>([\s\S]*?)<\/span>/gi, '$1')
            // Remove XML namespace declarations
            html = html.replace(/xmlns[^=]*="[^"]*"/gi, '')
            return html
        },

        insertImageFile(e) {
            const file = e.target.files[0]
            if (!file) return
            const reader = new FileReader()
            reader.onload = (ev) => {
                this.editor.chain().focus().setImage({ src: ev.target.result }).run()
            }
            reader.readAsDataURL(file)
            this.$refs.imgInput.value = ''
        },
    },
}
</script>

<style scoped>
.doc-editor {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: clip; /* clip for border-radius — NOT hidden, which breaks sticky */
}
.doc-editor--readonly {
    border-color: transparent;
}

/* Toolbar */
.doc-editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 5px 6px;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    position: sticky;
    top: 0;
    z-index: 2;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.doc-toolbar-group {
    display: flex;
    gap: 1px;
}
.doc-toolbar-sep {
    width: 1px;
    height: 20px;
    background: #ccc;
    margin: 0 3px;
}
.doc-editor-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 26px;
    padding: 0 4px;
    border-radius: 3px;
    cursor: pointer;
    color: #424242;
    font-family: monospace;
    user-select: none;
    transition: background 0.1s;
}
.doc-editor-btn:hover { background: #ddd; }
.doc-editor-btn.is-active {
    background: #1976d2;
    color: white;
}
.doc-editor-btn.is-active .v-icon { color: white !important; }
</style>

<style>
/* Global — editor prose styles */
.doc-editor-content .ProseMirror {
    padding: 12px 14px;
    min-height: 120px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 1.6;
    direction: ltr;
    text-align: left;
    outline: none;
}
.doc-editor-content .ProseMirror h1 { font-size: 1.6em; font-weight: 700; margin: 0.6em 0 0.3em; }
.doc-editor-content .ProseMirror h2 { font-size: 1.3em; font-weight: 700; margin: 0.6em 0 0.3em; }
.doc-editor-content .ProseMirror h3 { font-size: 1.1em; font-weight: 700; margin: 0.5em 0 0.2em; }
.doc-editor-content .ProseMirror p { margin: 0 0 0.5em; }
.doc-editor-content .ProseMirror ul,
.doc-editor-content .ProseMirror ol { padding-left: 1.5em; margin: 0.3em 0; }
.doc-editor-content .ProseMirror blockquote {
    border-left: 3px solid #bdbdbd;
    padding-left: 12px;
    color: #757575;
    margin: 0.5em 0;
}
.doc-editor-content .ProseMirror code {
    background: #f0f0f0;
    border-radius: 3px;
    padding: 1px 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.88em;
}
.doc-editor-content .ProseMirror pre {
    background: #1e1e2e;
    color: #cdd6f4;
    border-radius: 6px;
    padding: 12px 14px;
    margin: 0.6em 0;
    overflow-x: auto;
}
.doc-editor-content .ProseMirror pre code {
    background: transparent;
    color: inherit;
    padding: 0;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}
.doc-editor-content .ProseMirror img {
    max-width: 100%;
    border-radius: 4px;
    margin: 6px 0;
    cursor: default;
}
.doc-editor-content .ProseMirror hr {
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 1em 0;
}
.doc-editor-content .ProseMirror:focus { outline: none; }
</style>
